import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Bug, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportProblemModal = ({ isOpen, onClose, page = 'general' }) => {
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    { value: 'bug', label: 'Bug / Something not working', icon: Bug, color: 'text-red-600' },
    { value: 'feature_issue', label: 'Feature not working as expected', icon: AlertCircle, color: 'text-orange-600' },
    { value: 'performance', label: 'Slow performance', icon: Zap, color: 'text-yellow-600' },
    { value: 'other', label: 'Other issue', icon: AlertCircle, color: 'text-gray-600' },
  ];

  const handleSubmit = async () => {
    if (!category) {
      toast.error('Please select a problem category');
      return;
    }

    if (!description.trim()) {
      toast.error('Please describe the problem');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/feedback/submit`,
        {
          type: 'bug_report',
          category,
          page,
          feedback_text: description,
        },
        { withCredentials: true }
      );

      toast.success('Problem report submitted! We\'ll look into it. 🔧');
      setCategory('');
      setDescription('');
      onClose();
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2">
            <Bug className="h-6 w-6 text-red-600" />
            Report a Problem
          </DialogTitle>
          <DialogDescription className="text-center">
            Help us fix issues and improve HealthLoop
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Problem Category */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              What type of problem are you experiencing? *
            </label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select problem type" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${cat.color}`} />
                        <span>{cat.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Problem Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Describe the problem *
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide as much detail as possible:&#10;- What were you trying to do?&#10;- What happened?&#10;- What did you expect to happen?"
              className="min-h-[150px] resize-none"
              maxLength={1000}
            />
            <p className="text-xs text-gray-500 text-right">
              {description.length}/1000 characters
            </p>
          </div>

          {/* Current Page Info */}
          {page && page !== 'general' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-900">
                <strong>📍 Page:</strong> {page}
              </p>
            </div>
          )}

          {/* Privacy Note */}
          <div className="bg-healthloop-mint-50 border border-healthloop-mint-200 rounded-lg p-3">
            <p className="text-xs text-healthloop-mint-900">
              <strong>🔒 Confidential:</strong> We'll review this report promptly and keep your information private.
            </p>
          </div>

          {/* Submit Button */}
          <div className="space-y-2">
            <Button
              onClick={handleSubmit}
              disabled={submitting || !category || !description.trim()}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              size="lg"
            >
              <Bug className="h-4 w-4 mr-2" />
              {submitting ? 'Submitting...' : 'Submit Problem Report'}
            </Button>
            <Button onClick={onClose} variant="ghost" className="w-full">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportProblemModal;
